import React, {useMemo} from 'react'
import {Box, Typography, Tooltip} from '@mui/material'
import {getOrderStatus} from '../../helpers/UtilityHelper'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, SUBTLE, STATUS_COLORS, STATUS_META} from '../../styles/foundation/colors'
import {softCardSx, softCardTitleSx, softCardSubSx} from './dashboardStyles'

/**
 * DeliverySuccessDonut — Soft-Segmented status view for the overview.
 * A conic-gradient donut (delivery-success % in the center) beside a legend of every
 * status with count + a proportional mini-bar. Uses the fixed semantic palette.
 */
const TRACK = '#f1f4f8'

export default function DeliverySuccessDonut({data = [], subtitle, flow = 'ALL'}) {
  const isReverse = flow === 'REVERSE';
  const {segments, total, deliveredPct, maxCount} = useMemo(() => {
    const counts = {}
    ;(Array.isArray(data) ? data : []).forEach((s) => {
      const st = getOrderStatus(s.orderStatus)
      if (st === 'UNKNOWN') return
      counts[st] = (counts[st] || 0) + 1
    })
    const tot = Object.values(counts).reduce((a, b) => a + b, 0)
    const meta = isReverse
      ? STATUS_META.filter((m) => m.key !== 'RTO').map((m) => m.key === 'DELIVERY_FAILED' ? {...m, label: 'NPR / Failed'} : m)
      : STATUS_META;
    const segs = meta.filter((m) => counts[m.key]).map((m) => ({
      ...m, count: counts[m.key], pct: tot ? (counts[m.key] / tot) * 100 : 0, color: STATUS_COLORS[m.key],
    }))
    return {
      segments: segs, total: tot,
      deliveredPct: tot ? ((counts.DELIVERED || 0) / tot) * 100 : 0,
      maxCount: Math.max(...segs.map((s) => s.count), 1),
    }
  }, [data, isReverse])

  // Build the conic-gradient stops in display order.
  let acc = 0
  const stops = segments.map((s) => {
    const from = acc
    acc += s.pct
    return `${s.color} ${from.toFixed(2)}% ${acc.toFixed(2)}%`
  }).join(', ')

  return (
    <Box sx={softCardSx}>
      <Typography sx={softCardTitleSx}>Delivery Success</Typography>
      <Typography sx={{...softCardSubSx, mb: 2}}>{subtitle || `outcome share of ${total.toLocaleString()} shipments`}</Typography>

      {total === 0 ? (
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, py: 2}}>
          No shipments in the selected range.
        </Typography>
      ) : (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap'}}>
          <Box sx={{
            width: 150, height: 150, borderRadius: '50%', flex: 'none',
            display: 'grid', placeItems: 'center', position: 'relative',
            background: `conic-gradient(${stops})`,
            '&::after': {content: '""', position: 'absolute', width: 96, height: 96, bgcolor: '#fff', borderRadius: '50%'},
          }}>
            <Box sx={{position: 'relative', zIndex: 1, textAlign: 'center'}}>
              <Typography sx={{fontFamily: FONT, fontSize: '26px', fontWeight: WEIGHT.extra, color: STATUS_COLORS.DELIVERED, lineHeight: 1}}>
                {deliveredPct.toFixed(0)}%
              </Typography>
              <Typography sx={{fontFamily: FONT, fontSize: '10px', color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                Delivered
              </Typography>
            </Box>
          </Box>

          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.1, flex: 1, minWidth: 200}}>
            {segments.map((s) => (
              <Box key={s.key} sx={{display: 'grid', gridTemplateColumns: '92px 1fr 42px', gap: 1.2, alignItems: 'center'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
                  <Box sx={{width: 9, height: 9, borderRadius: '3px', bgcolor: s.color, flex: 'none'}} />
                  {s.label}
                </Box>
                <Tooltip arrow title={`${s.label}: ${s.count.toLocaleString()} shipment${s.count === 1 ? '' : 's'} · ${s.pct.toFixed(1)}% of total`}>
                  <Box sx={{height: 8, bgcolor: TRACK, borderRadius: '5px', overflow: 'hidden', cursor: 'default'}}>
                    <Box sx={{height: '100%', width: `${(s.count / maxCount) * 100}%`, bgcolor: s.color, borderRadius: '5px'}} />
                  </Box>
                </Tooltip>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY, textAlign: 'right'}}>
                  {s.count.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}
